import { useApplicationsTableState } from 'components/Dashboard/ApplicationsTable/Context/ApplicationsTableState.context';
import { useDashboard } from 'components/Dashboard/Dashboard.context';
import {
  getExpiringDateRange,
  isExpiring,
} from 'components/Dashboard/DashboardHeader/QuickFilters/QuickFilters.helpers';
import { useAmplitude } from 'hooks/useAmplitude';
import { TIntroducerApplication } from 'lendingApi/applications.types';
import { TFilter } from 'types';
import { APPLICATION_STATUS } from 'utils/applications/applications.config';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';

export const useQuickFilters = () => {
  const {
    table: { getColumn, getPreFilteredRowModel },
  } = useApplicationsTableState();

  const { isLoading, isError, state } = useDashboard();
  const { trackEvent } = useAmplitude();

  const statusFilter = getColumn('status');
  const expiryDateFilter = getColumn('expiryDate');
  const [expiringFromDate, expiringToDate] = getExpiringDateRange();

  const currentStatusValues = statusFilter?.getFilterValue() as
    | string[]
    | undefined;

  const currentExpiryValues = expiryDateFilter?.getFilterValue() as
    | [string, string]
    | undefined;

  const handleClick = (option: TFilter, quantity: number) => {
    trackEvent(AMPLITUDE_EVENTS.quickFilterClicked, {
      'Filter name': option.id,
      'Application count': quantity,
    });
    if (option.id === 'all') {
      statusFilter?.setFilterValue(undefined);
      expiryDateFilter?.setFilterValue(undefined);
      return;
    }

    statusFilter?.setFilterValue([...option.propertiesToFilterOn]);

    if (option.id === 'expiring') {
      expiryDateFilter?.setFilterValue([expiringFromDate, expiringToDate]);
    } else {
      expiryDateFilter?.setFilterValue(undefined);
    }
  };

  const isFilterActive = (option: TFilter) => {
    if (option.id === 'all') {
      return !currentStatusValues?.length && !currentExpiryValues;
    }

    if (option.id === 'expiring') {
      if (!currentExpiryValues) return false;

      // TODO: when we remove isExpiring from FILTER_OPTIONS, change back to using the option's propertiesToFilterOn instead https://iwoca.atlassian.net/browse/BAE-3088
      const EXPIRING_STATUS_VALUES: TIntroducerApplication['status'][] = [
        APPLICATION_STATUS.offered,
        APPLICATION_STATUS.conditional_offer,
        APPLICATION_STATUS.talking_to_customer,
      ];

      const [currentFrom, currentTo] = currentExpiryValues;

      const matchesDateRange =
        currentFrom === expiringFromDate && currentTo === expiringToDate;

      const matchesStatus = EXPIRING_STATUS_VALUES?.every((property) =>
        currentStatusValues?.includes(property),
      );

      return matchesStatus && matchesDateRange;
    }

    return (
      option.propertiesToFilterOn?.every((property) =>
        currentStatusValues?.includes(property),
      ) && !currentExpiryValues
    );
  };

  const getFilteredCount = (option: TFilter) => {
    const preFilteredRows = getPreFilteredRowModel().rows;
    const filteredRows = preFilteredRows.filter((row) => {
      const matchesStatus = option.propertiesToFilterOn.includes(
        row.original.status,
      );
      if (option.id === 'expiring') {
        const expiry =
          row.original.formal_offer_sets?.[0]?.formal_offers?.[0]?.timeout;
        return matchesStatus && expiry && isExpiring(expiry);
      }
      return matchesStatus;
    });

    return filteredRows.length;
  };

  return {
    isLoading: !isError && (isLoading || state.applications === undefined),
    getFilteredCount,
    isFilterActive,
    handleClick,
  };
};
